import { ArrowLeft } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { useCreateFirmMutation } from '@/services/api/firmsService';
import {
  CreateFirmPayload,
  UpdateFirmPayload,
} from '@/services/types/firms-types';

import { FirmContainer } from '../FirmContainer';

/**
 * FirmCreatePage is the top-level page component for creating a new firm.
 *
 * This component is responsible for:
 * - Providing the page layout and navigation elements
 * - Managing API interactions via RTK mutation hooks
 * - Handling success/error messaging via toast notifications
 * - Navigating the user after successful creation
 *
 * It delegates the actual form rendering and state management to the FirmContainer component,
 * focusing on the page-level concerns and API integration.
 */
export const FirmCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const [createFirm, { isLoading: isSubmitting, error }] =
    useCreateFirmMutation();

  /**
   * Handles form submission by calling the API and showing appropriate notifications
   */
  const handleSubmit = async (
    values: CreateFirmPayload | UpdateFirmPayload,
  ) => {
    try {
      // Clean the payload by removing null values and ensuring correct types
      const createPayload: CreateFirmPayload = Object.entries(values).reduce(
        (acc, [key, value]) => {
          // Skip id field as it's not needed for creation
          if (key === 'id') return acc;

          // Only include non-null values
          if (value !== null && value !== undefined && value !== '') {
            return { ...acc, [key]: value };
          }
          return acc;
        },
        {} as CreateFirmPayload,
      );

      const newFirm = await createFirm(createPayload).unwrap();
      toast.success('Firm created successfully');
      navigate(`/firms/${newFirm.id}`);
    } catch (err) {
      console.error('Error creating firm:', err);
      toast.error('Failed to create firm');
    }
  };

  /**
   * Navigates back to the firms list when cancel is clicked
   */
  const handleCancel = () => {
    navigate('/firms');
  };

  // Create a header for the form
  const formHeader = (
    <div className="border-b p-4">
      <h2 className="text-lg font-semibold">Create New Firm</h2>
    </div>
  );

  return (
    <div className="py-6 space-y-6 ">
      <div className="flex items-center gap-4 mb-6">
        <Button
          variant="ghost"
          size="icon"
          onClick={handleCancel}
          aria-label="Back to firms list"
          className="h-8 w-8"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-md mb-6">
          {error.toString()}
        </div>
      )}

      <FirmContainer
        initialMode="create"
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isSubmitting={isSubmitting}
        header={formHeader}
      />
    </div>
  );
};
